import classNames from "classnames";
import Icon from "components/Common/Icon";
import { MediaQuery } from "@outplayed/responsive";
import { getWowStatic } from "hooks/wow/getStaticData";
import { getEnchantName, getEnchantQuality, getEnchantQualityIconSrc, getEnchantImgLinkSrc, trimQuality } from "lib/enchant-ids";
import { getFormattedContentType, getFormattedNames, getIsHealer } from "lib/wowHelpers";
import React, { useState } from "react";
import SubtitleAndDescription from "../Common/SubtitleAndDescription";
import WowTooltip from "../Tooltips/WowTooltip";
import ArmorItemCombo from "./ArmorItemCombo";
import WeaponItem from "./WeaponItem";

const PvEGearGuide = (props) => {
  const { classType, specType, encounterType } = props;
  const { displayClassName, displaySpecName } = getFormattedNames(classType, specType);
  // bis = Best In Slot, pop = Most Popular
  const [stat, setStat] = useState("pop");

  const specClassName = `${displaySpecName} ${displayClassName}`;
  const contentType = getFormattedContentType(encounterType);

  return (
    <div className="wow-items-section content-section">
      <div className="header-section">
        <div className="title-and-button">
          <SubtitleAndDescription subtitle={`${stat === "bis" ? "BIS" : "Most Popular"} ${specClassName} Gear`} />
          {/* <MediaQuery min="MOBILE_LARGE" max="DESKTOP_LARGE" renderNullOnFail>
            <ToggleButton stat={stat} setStat={setStat} />
          </MediaQuery> */}
        </div>
        <div className="text-blurb">
          The {stat === "bis" ? "Best in Slot" : "Most Popular"} Gear used by the best {specClassName} players. Gear in each slot
          is determined by analyzing the top {specClassName} {contentType} rankings. The selected piece of gear is the{" "}
          {stat === "bis" ? "most performant" : "most popular"} item for that slot.{" "}
          {stat === "bis"
            ? "For tier piece slots, we've added a 10% popularity threshold in order to guarantee that at least 4/5 of the best tier set armor pieces are recommended."
            : ""}
        </div>
        {/* <MediaQuery min="MOBILE_SMALL" max="MOBILE_MEDIUM" renderNullOnFail>
          <ToggleButton stat={stat} setStat={setStat} />
        </MediaQuery> */}
      </div>
      <WOWArmorLayoutContainer {...props} stat={stat} />
      <WOWWeaponLayoutContainer {...props} stat={stat} />
    </div>
  );
};

const ToggleButton = (props) => {
  const { stat, setStat } = props;

  return (
    <div className="toggle-button-container">
      <div
        className={classNames("toggle-button left", {
          active: stat === "pop",
        })}
        onClick={() => setStat("pop")}
      >
        Most Popular
      </div>
      <div
        className={classNames("toggle-button right", {
          active: stat === "bis",
        })}
        onClick={() => setStat("bis")}
      >
        Best in Slot
      </div>
    </div>
  );
};

const WOWArmorLayoutContainer = (props) => {
  const firstColumnItems = ["head", "neck", "shoulder", "cape", "chest", "shirt", "tabard", "wrist"];
  const secondColumnItems = ["gloves", "belt", "legs", "feet"];
  const comboItems = ["ring1_ring2", "trinket1_trinket2"];

  return (
    <div className="armor-layout-container">
      <WOWArmorLayoutColumn {...props} itemSlots={firstColumnItems} isLeftSide />
      <WOWArmorLayoutColumn {...props} itemSlots={secondColumnItems} comboItems={comboItems} />
    </div>
  );
};

const WOWArmorLayoutColumn = (props) => {
  const { builds, combos, itemSlots, isLeftSide, stat, specType, comboItems, encounterType } = props;

  const itemType = stat === "bis" && getIsHealer(specType) ? "hps_item" : stat === "bis" ? "dps_item" : "popularity_item";

  const itemData = itemSlots.map((slot) => {
    if (!builds) {
      return {
        slot,
      };
    }

    const allItemData = builds[slot];
    const itemInSlot = allItemData?.[itemType];

    if (itemInSlot) {
      const id = itemInSlot.item_id;
      return {
        id: id,
        quality: itemInSlot.quality,
        slot,
        popPerc: parseFloat(itemInSlot.perc).toFixed(1) + "%",
        popCount: itemInSlot.count,
        enchant: itemInSlot.enchant,
      };
    }

    return {
      slot,
    };
  });

  return (
    <div className="armor-layout">
      {itemData.map((item, index) => {
        return (
          <ArmorItem
            key={index}
            id={item.id}
            isLeftSide={isLeftSide}
            // itemColor={getItemQualityColor(item.quality)}
            itemColor={item.quality === 5 ? "#ff8000" : "rgb(163, 53, 238)"}
            popPerc={item.popPerc}
            popCount={item.popCount}
            enchantName={item.enchant}
            slot={item.slot}
          />
        );
      })}
      {comboItems?.map((item, index) => {
        return <ArmorItemCombo key={index} data={combos?.[item][itemType][0]} encounterType={encounterType} />;
      })}
    </div>
  );
};

const ArmorItem = (props) => {
  const { isLeftSide, id, popPerc, popCount, enchantName, slot } = props;
  let { itemName = "Cosmetic", itemColor, className } = props;
  let img;

  const staticData = getWowStatic("item", id);
  if (!staticData || !staticData.name) {
    if (slot === "shirt" || slot === "tabard") {
      img = `https://static.bigbrain.gg/wow/icons/empty-slots/${slot}.png`;
    } else {
      itemName = "";
    }

    itemColor = "#717171";
    className = "empty-slot";
  } else {
    itemName = staticData.name;
    img = staticData.img;
  }

  return (
    <div className={isLeftSide ? `armor-item ${className}` : `armor-item-reversed ${className}`} item-id={id}>
      <div className="armor-item-container">
        <WowTooltip type="item" id={id}>
          <Icon staticDataImg={img} color={itemColor} classType="bis-item-icon" />
        </WowTooltip>
        <div className={isLeftSide ? "item-info" : "item-info-right"}>
          <div className="item-name" style={{ color: itemColor }}>
            {itemName}
          </div>
          <div className="enchant-info">
            {enchantName ? <Icon iconSize="18px" staticDataImg={getEnchantImgLinkSrc(enchantName)} /> : null}
            <div className="enchant-text">{trimQuality(getEnchantName(enchantName?.split(" (")[0]))}</div>
            {enchantName ? (
              <img
                style={
                  getEnchantQuality(enchantName) == 2
                    ? { width: "16px", height: "8px", marginLeft: "4px" }
                    : { width: "16px", height: "12px", marginLeft: "4px" }
                }
                src={getEnchantQualityIconSrc(enchantName)}
              ></img>
            ) : null}
          </div>
        </div>
      </div>
      {itemName !== "Cosmetic" && (
        <div className="popularity-container">
          <div className="popularity-value">{popPerc}</div>
          <div className="popularity-label">({popCount?.toLocaleString("en-US")} parses)</div>
        </div>
      )}
    </div>
  );
};

const WOWWeaponLayoutContainer = (props) => {
  const { combos, stat, specType } = props;
  const weapons =
    stat === "bis" && getIsHealer(specType)
      ? combos?.weapon1_weapon2?.hps_item[0]
      : stat === "bis"
      ? combos?.weapon1_weapon2?.dps_item[0]
      : combos?.weapon1_weapon2?.popularity_item[0];

  const weapon1Data = getWowStatic("item", weapons?.first_item_id);
  const weapon2Data = getWowStatic("item", weapons?.second_item_id);

  const weapon1ItemColor = weapons?.quality1 === 5 ? "#ff8000" : "rgb(163, 53, 238)";
  const weapon2ItemColor = weapons?.quality2 === 5 ? "#ff8000" : "rgb(163, 53, 238)";

  if (!weapon2Data) {
    return (
      <>
        <div className="weapon-combos-label">Weapon: Main Hand</div>
        <div className="weapon-layout-container">
          <div className="weapons">
            <WeaponItem
              positionLeft
              itemName={weapon1Data?.name}
              // itemColor={getItemQualityColor(weaponSlot1.quality)}
              itemColor={weapon1ItemColor}
              img={weapon1Data?.img}
              id={weapons?.first_item_id}
              enchantName={weapons?.first_item_enchant_name}
              popularity={weapons?.popularity}
              count={weapons?.count}
            />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="weapon-combos-label">Weapons: Main Hand & Off Hand</div>
      <div className="weapon-layout-container weapon-combos">
        <div className="weapons">
          <WeaponItem
            positionLeft
            itemName={weapon1Data?.name}
            // itemColor={getItemQualityColor(weaponSlot1.quality)}
            itemColor={weapon1ItemColor}
            img={weapon1Data?.img}
            id={weapons?.first_item_id}
            enchantName={weapons?.first_item_enchant_name}
          />
          <MediaQuery min="MOBILE_LARGE" max="DESKTOP_LARGE" renderNullOnFail>
            <div className="popularity-container">
              <div className="popularity-value">{parseFloat(weapons?.popularity).toFixed(1)}%</div>
              <div className="popularity-label">({weapons?.count.toLocaleString("en-US")} parses)</div>
            </div>
          </MediaQuery>
          <MediaQuery min="MOBILE_SMALL" max="MOBILE_MEDIUM" renderNullOnFail>
            <div className="horizontal-line" />
          </MediaQuery>
          <WeaponItem
            itemName={weapon2Data?.name}
            // itemColor={getItemQualityColor(weaponSlot1.quality)}
            itemColor={weapon2ItemColor}
            img={weapon2Data?.img}
            id={weapons?.second_item_id}
            enchantName={weapons?.second_item_enchant_name}
          />
        </div>
        <MediaQuery min="MOBILE_SMALL" max="MOBILE_MEDIUM" renderNullOnFail>
          <div className="popularity-container">
            <div className="popularity-value">{parseFloat(weapons?.popularity).toFixed(1)}%</div>
            <div className="popularity-label">({weapons?.count.toLocaleString("en-US")} parses)</div>
          </div>
        </MediaQuery>
      </div>
    </>
  );
};

export default PvEGearGuide;
